import React, { useEffect, useState } from "react";
import logo from "../assets/cloudvetx-logo-single.png";
import svglogo from "../assets/CloudVetX.svg";
import { LogIn, Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [passedHero, setPassedHero] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const isHome = location.pathname === "/";

  const handleSectionLinkClick = (event, sectionId) => {
    event.preventDefault();
    setMenuOpen(false); // close mobile menu on click
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: sectionId } });
    } else {
      scrollToSection(sectionId);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      setPassedHero(window.scrollY > window.innerHeight);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const useColoredLinks = !isHome || passedHero;

  return (
    <header className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-6xl px-4">
      <div
        className={`flex items-center justify-between px-6 md:px-10 py-3 rounded-full
          backdrop-blur-md border shadow-lg transition-all duration-300
          ${scrolled
            ? "bg-white/40 border-black/30"
            : "bg-white/20 border-transparent"
          }
        `}
      >
        {/* Logo */}
        <a href="/" className="flex items-center space-x-1">
          <img
            src={logo}
            alt="CloudVetX Logo"
            className="h-10 w-10 object-contain"
          />
          <span className="text-lg md:text-xl font-bold" aria-label="CloudVetX">
            <span
              style={{
                color: useColoredLinks ? "#064e3b" : "#fff",
                textShadow: useColoredLinks ? "none" : "0 1px 4px rgba(0,0,0,0.4)",
              }}
            >
              Cloud
            </span>
            <span
              style={{
                color: useColoredLinks ? "#0c8560" : "#b8e7c4",
                textShadow: useColoredLinks ? "none" : "0 1px 4px rgba(0,0,0,0.4)",
              }}
            >
              Vet
            </span>
            <span
              style={{
                color: "#fff",
                backgroundColor: useColoredLinks ? "#064e3b" : "transparent",
                borderRadius: useColoredLinks ? "0.2em" : "0",
                padding: useColoredLinks ? "0 0.16em" : "0",
                textShadow: useColoredLinks ? "none" : "0 1px 4px rgba(0,0,0,0.4)",
              }}
            >
              X
            </span>
          </span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center space-x-2">
          {["Features", "About", "Pilot", "Contact"].map((item) => (
            <a
              key={item}
              href={`/#${item === "Pilot" ? "pilot" : item.toLowerCase()}`}
              onClick={(event) => handleSectionLinkClick(event, item === "Pilot" ? "pilot" : item.toLowerCase())}
              className={`px-4 py-1 rounded-full transition font-medium
                ${useColoredLinks
                  ? "text-[#064e3b] hover:text-emerald-700 hover:bg-emerald-50"
                  : "text-white/80 hover:text-white hover:bg-white/10"
                }
              `}
            >
              {item}
            </a>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href="/#contact"
            onClick={(event) => handleSectionLinkClick(event, "contact")}
            className="bg-[#064e3b] hover:bg-[#10b981] text-white font-semibold text-sm px-8 py-4 rounded-2xl shadow-lg transition"
          >

            Ask About the Pilot
          </a>
          <a
            href="https://app.cloudvetx.com/"
            target="_blank"
            rel="noopener noreferrer"
            title="Login to CloudVetX App"
            aria-label="Login to CloudVetX App"
            className={`bg-[#064e3b] hover:bg-[#10b981] text-white font-semibold px-8 py-4 rounded-2xl shadow-lg transition
            `}
          >
            <LogIn size={20} />
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="cvx-mobile-menu-button lg:hidden p-2 rounded-lg"
          aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={menuOpen}
          aria-controls="mobile-navigation"
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div id="mobile-navigation" className="absolute top-full mt-3 w-full max-w-6xl px-4 lg:hidden">
          <div className="flex flex-col gap-2 p-4 rounded-2xl bg-white shadow-lg border">
            {["Features", "About", "Pilot", "Contact"].map((item) => (
              <a
                key={item}
                href={`/#${item === "Pilot" ? "pilot" : item.toLowerCase()}`}
                onClick={(event) => handleSectionLinkClick(event, item === "Pilot" ? "pilot" : item.toLowerCase())}
                className="px-4 py-2 text-left text-[#064e3b] font-medium hover:bg-emerald-50 rounded-lg"
              >
                {item}
              </a>
            ))}

            <div className="border-t my-2" />

            <a
              href="/#contact"
              onClick={(event) => handleSectionLinkClick(event, "contact")}
              className="w-full btn-primary"
            >
              Ask About the Pilot
            </a>
            <a
              href="https://app.cloudvetx.com/"
              target="_blank"
              rel="noopener noreferrer"
              title="Login to CloudVetX App"
              className="btn-primary"
            >
              <div className="flex items-center justify-center gap-2">
                <LogIn size={18} /> Login
              </div>
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
